import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { SkipForward, Calendar, User, MapPin, TrendingDown, AlertCircle, Filter } from "lucide-react";

const DOW_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function fmtDT(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso || ""; } }

/**
 * Founder-only Skip History dashboard.
 *  - Table of every skip event
 *  - Group-by-weekday view so "cook absent every Monday" pops out
 *  - Group-by-title view so "House cleaning" recidivism jumps out
 */
export default function SkipHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("");
  const [taskFilter, setTaskFilter] = useState("");
  const [view, setView] = useState("weekday"); // 'weekday' | 'title' | 'table'

  useEffect(() => {
    api.get("/tasks/skip-history?limit=2000")
      .then(r => setRows(r.data || []))
      .catch(e => toast.error(e?.response?.data?.detail || "Could not load skip history"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => rows.filter(r =>
    (!roleFilter || r.assignee_role === roleFilter)
    && (!taskFilter || (r.task_title || "").toLowerCase().includes(taskFilter.toLowerCase()))
  ), [rows, roleFilter, taskFilter]);

  const byWeekday = useMemo(() => {
    const buckets = {};
    for (let i = 0; i < 7; i++) buckets[i] = [];
    for (const r of filtered) {
      const d = typeof r.skip_dow === "number" ? r.skip_dow : new Date(r.skip_date).getDay();
      // Python weekday: Monday=0. JS getDay: Sunday=0. Trust python's `skip_dow`.
      const idx = typeof r.skip_dow === "number" ? r.skip_dow : ((d + 6) % 7);
      if (buckets[idx]) buckets[idx].push(r);
    }
    return buckets;
  }, [filtered]);

  const byTitle = useMemo(() => {
    const map = {};
    for (const r of filtered) {
      const k = r.task_title || "(untitled)";
      if (!map[k]) map[k] = [];
      map[k].push(r);
    }
    return Object.entries(map).sort((a, b) => b[1].length - a[1].length);
  }, [filtered]);

  const roles = Array.from(new Set(rows.map(r => r.assignee_role).filter(Boolean)));
  const peakDay = useMemo(() => {
    let best = { idx: -1, count: 0 };
    Object.entries(byWeekday).forEach(([idx, arr]) => {
      if (arr.length > best.count) best = { idx: parseInt(idx), count: arr.length };
    });
    return best;
  }, [byWeekday]);

  return (
    <div className="space-y-5" data-testid="skip-history-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Founder-only</div>
        <h1 className="text-2xl sm:text-3xl font-semibold flex items-center gap-2">
          <SkipForward className="text-brand" /> Skip History
        </h1>
        <p className="text-sm text-[color:var(--aii-text-2)] mt-1">Every task-skip logged by the team, grouped so patterns like &ldquo;cook absent every Monday&rdquo; pop out.</p>
      </div>

      {/* Peak-day headline */}
      {peakDay.idx >= 0 && peakDay.count >= 3 && (
        <div className="aii-card p-4 border-l-4 border-amber-500 flex items-center gap-3" data-testid="skip-peak-card">
          <AlertCircle className="text-amber-600 shrink-0" size={22}/>
          <div className="text-sm">
            Most skips fall on <b>{DOW_LABELS[peakDay.idx]}</b> ({peakDay.count} skips). Consider revisiting the roster or vendor for that day.
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="aii-card p-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-[color:var(--aii-muted)]"><Filter size={13}/> Filter:</div>
        <select data-testid="skip-role-filter" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
          className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white">
          <option value="">All roles</option>
          {roles.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <input data-testid="skip-title-filter" value={taskFilter} onChange={e => setTaskFilter(e.target.value)}
          placeholder="Search task title…"
          className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white flex-1 min-w-[150px]"/>
        <div className="ml-auto inline-flex rounded border border-[color:var(--aii-border)] overflow-hidden">
          {[["weekday","By weekday"],["title","By task"],["table","Table"]].map(([k, l]) => (
            <button key={k} data-testid={`skip-view-${k}`} onClick={() => setView(k)}
              className={`px-3 py-1 text-xs font-semibold ${view === k ? "bg-brand text-white" : "bg-white"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="aii-card p-10 text-center text-[color:var(--aii-muted)]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="aii-card p-10 text-center text-[color:var(--aii-muted)]" data-testid="skip-empty">
          No skips logged in the current filter. The team is holding the line.
        </div>
      ) : view === "weekday" ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="skip-by-weekday">
          {DOW_LABELS.map((day, idx) => {
            const items = byWeekday[idx] || [];
            const isPeak = peakDay.idx === idx && peakDay.count >= 3;
            return (
              <div key={day} className={`aii-card p-3 ${isPeak ? "border-2 border-amber-400" : ""}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-sm">{day}</div>
                  <span className="text-xs text-[color:var(--aii-muted)]">{items.length}</span>
                </div>
                {items.length === 0 ? (
                  <div className="text-[11px] text-[color:var(--aii-muted)]">No skips</div>
                ) : (
                  <ul className="space-y-1 max-h-64 overflow-y-auto">
                    {items.slice(0, 20).map((r) => (
                      <li key={r.id} className="text-[11px] leading-tight border-b border-[color:var(--aii-border)]/50 pb-1">
                        <div className="font-medium truncate" title={r.task_title}>{r.task_title}</div>
                        <div className="text-[color:var(--aii-muted)] flex items-center gap-1.5 flex-wrap">
                          <User size={9}/> {r.skipped_by_name || "?"}
                          {r.reason && <span className="italic truncate flex-1" title={r.reason}>&mdash; &ldquo;{r.reason}&rdquo;</span>}
                        </div>
                      </li>
                    ))}
                    {items.length > 20 && <li className="text-[10px] text-[color:var(--aii-muted)]">+{items.length - 20} more…</li>}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      ) : view === "title" ? (
        <div className="aii-card p-4" data-testid="skip-by-title">
          {/* Stacked bar per task — shows what percent of skips fall on
              each reason chip so "House cleaning · 60% Cook absent"
              becomes visually obvious. */}
          <div className="space-y-3">
            {byTitle.map(([title, arr]) => {
              const reasonCounts = arr.reduce((acc, x) => {
                const key = (x.reason || "no reason").trim() || "no reason";
                acc[key] = (acc[key] || 0) + 1;
                return acc;
              }, {});
              const total = arr.length;
              const sorted = Object.entries(reasonCounts).sort((a, b) => b[1] - a[1]);
              const palette = ["#1B7A3E","#f59e0b","#dc2626","#6366f1","#0ea5e9","#a855f7","#14b8a6","#78716c"];
              return (
                <div key={title} className="border border-[color:var(--aii-border)] rounded p-3">
                  <div className="flex items-baseline justify-between gap-2 mb-2">
                    <div className="font-semibold text-sm truncate" title={title}>{title}</div>
                    <div className="text-xs text-[color:var(--aii-muted)] shrink-0">{total} skip{total === 1 ? "" : "s"}</div>
                  </div>
                  {/* Stacked bar */}
                  <div className="flex h-3 rounded overflow-hidden border border-[color:var(--aii-border)] bg-white">
                    {sorted.map(([r, c], i) => (
                      <div key={r}
                           style={{ width: `${(c / total) * 100}%`, background: palette[i % palette.length] }}
                           title={`${r}: ${c} (${((c / total) * 100).toFixed(0)}%)`}/>
                    ))}
                  </div>
                  {/* Legend */}
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px]">
                    {sorted.slice(0, 6).map(([r, c], i) => (
                      <span key={r} className="inline-flex items-center gap-1">
                        <span className="w-2.5 h-2.5 rounded" style={{ background: palette[i % palette.length] }}/>
                        <span className="text-[color:var(--aii-text-2)] truncate max-w-[180px]" title={r}>{r}</span>
                        <span className="text-[color:var(--aii-muted)]">· {((c / total) * 100).toFixed(0)}%</span>
                      </span>
                    ))}
                    {sorted.length > 6 && <span className="text-[color:var(--aii-muted)]">+{sorted.length - 6} more</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="aii-card overflow-x-auto" data-testid="skip-table">
          <table className="aii-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Task</th>
                <th>Role</th>
                <th>Office</th>
                <th>Skipped by</th>
                <th>Reason</th>
                <th>Next due</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} data-testid={`skip-row-${r.id}`}>
                  <td className="text-xs text-[color:var(--aii-muted)]">{fmtDT(r.created_at)}</td>
                  <td className="max-w-[220px]"><div className="truncate" title={r.task_title}>{r.task_title}</div></td>
                  <td>{r.assignee_role}</td>
                  <td className="text-xs">{[r.city, r.destination].filter(Boolean).join(", ") || "—"}</td>
                  <td className="text-xs">{r.skipped_by_name || "—"}</td>
                  <td className="text-xs italic max-w-[260px]"><div className="truncate" title={r.reason}>{r.reason || <span className="text-[color:var(--aii-muted)]">—</span>}</div></td>
                  <td className="text-xs">{r.next_due_date || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
